import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "utils/axios";
import SidePanel from "layouts/SidePanel";
import ProfileAccount from "./components/ProfileAccount";
import { IconGlyph, themeTokens } from "./components/ProfileDashboardUI";
import { mapProfileUser } from "./profileDashboardData";
import { GET_USERS_DATA_BY_IDS } from "config/services";

const normalizeUserId = (value) => {
  if (value && typeof value === "object") {
    return value.userId ?? value.id ?? value.managerId ?? value.approverId ?? value.value ?? value._id;
  }
  return value;
};

const collectIdsFromList = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => normalizeUserId(item))
    .map((userId) => Number(userId))
    .filter((userId) => Number.isFinite(userId));

const collectApprovalUserIds = (user) => {
  const userIds = new Set();

  collectIdsFromList(user?.managers || user?.userProfile?.managers || []).forEach((userId) => {
    userIds.add(userId);
  });

  const approvals = user?.userEntity?.approvals || user?.entity?.approvals || [];
  approvals.forEach((approval) => {
    (approval?.businessDivisions || []).forEach((division) => {
      collectIdsFromList(division?.approvers || []).forEach((userId) => {
        userIds.add(userId);
      });
    });
  });

  return Array.from(userIds);
};

const MyProfileContent = () => {
  const authUser = useSelector((state) => state.auth?.user);
  const businessProfile = useSelector((state) => state.business?.profile);
  const [theme, setTheme] = useState("light");
  const [editing, setEditing] = useState(false);
  const [approvalUserDirectory, setApprovalUserDirectory] = useState({});
  const tokens = useMemo(() => themeTokens(theme), [theme]);
  const defaultUser = useMemo(
    () => mapProfileUser(authUser, businessProfile, approvalUserDirectory),
    [authUser, businessProfile, approvalUserDirectory],
  );
  const [userData, setUserData] = useState(defaultUser);

  React.useEffect(() => {
    const userIds = collectApprovalUserIds(authUser);

    if (userIds.length === 0) {
      setApprovalUserDirectory({});
      return;
    }

    let isMounted = true;

    const fetchApprovalUsers = async () => {
      try {
        const response = await axios.post(
          GET_USERS_DATA_BY_IDS,
          { userIds },
          { headers: { Authorization: localStorage.getItem("accessToken") || "" } },
        );
        const users = response?.data?.payload || response?.data?.data || [];
        const userDirectory = users.reduce((acc, user) => {
          if (user?.userId) acc[String(user.userId)] = user;
          return acc;
        }, {});

        if (isMounted) setApprovalUserDirectory(userDirectory);
      } catch (error) {
        if (isMounted) setApprovalUserDirectory({});
      }
    };

    fetchApprovalUsers();

    return () => {
      isMounted = false;
    };
  }, [authUser]);

  React.useEffect(() => {
    setUserData(defaultUser);
  }, [defaultUser]);

  const handleSave = (form) => {
    setUserData((current) => ({
      ...current,
      ...form,
      initials: mapProfileUser({ name: form.name }, {}).initials,
    }));
    setEditing(false);
  };

  return (
    <div className="min-h-screen font-sans" style={{ background: tokens.pageBackground, color: tokens.ink }}>
      <nav
        className="sticky top-0 z-30 flex items-center justify-between px-5 h-[52px]"
        style={{ backgroundColor: tokens.card, borderBottom: `1px solid ${tokens.line}` }}
      >
        <div>
          <h1 className="text-base font-semibold tracking-tight">My Profile</h1>
          <p className="text-[11px]" style={{ color: tokens.faint }}>
            Manage your profile, account details and approval hierarchy.
          </p>
        </div>
        <button
          onClick={() => setTheme((current) => (current === "light" ? "dark" : "light"))}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition-colors"
          style={{ borderColor: tokens.line, color: tokens.muted, backgroundColor: tokens.card }}
        >
          <IconGlyph name={theme === "light" ? "moon" : "sun"} className="w-3.5 h-3.5" />
          {theme === "light" ? "Dark" : "Light"}
        </button>
      </nav>

      <main className="mx-auto px-5 pb-16 pt-6">
        <div
          className="relative mb-4 flex items-center gap-4 overflow-hidden rounded-2xl p-6"
          style={{
            background:
              theme === "light"
                ? "radial-gradient(circle at 88% 18%, rgba(24,95,165,0.18), transparent 28%), linear-gradient(135deg, #FFF4E8 0%, #FFFFFF 48%, #EAF3FF 100%)"
                : "radial-gradient(circle at 88% 18%, rgba(253,102,0,0.18), transparent 30%), linear-gradient(135deg, #24201B 0%, #1B1E22 52%, #16253A 100%)",
            border: `1px solid ${tokens.line}`,
            boxShadow: "0 12px 32px rgba(24, 95, 165, 0.10), 0 2px 8px rgba(0,0,0,.05)",
          }}
        >
          <div
            className="absolute -right-10 -top-12 h-32 w-32 rounded-full"
            style={{ backgroundColor: theme === "light" ? "rgba(253,102,0,0.12)" : "rgba(253,102,0,0.10)" }}
          />
          <div
            className="absolute bottom-0 right-24 h-16 w-40 rounded-t-full"
            style={{ backgroundColor: theme === "light" ? "rgba(24,95,165,0.08)" : "rgba(181,212,244,0.08)" }}
          />
          <div
            className="relative z-10 flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 text-xl font-bold"
            style={{
              backgroundColor: theme === "light" ? "#FFFFFF" : tokens.primaryLight,
              borderColor: theme === "light" ? "#FDBA74" : tokens.primaryMid,
              color: tokens.primary,
              boxShadow: "0 10px 24px rgba(24, 95, 165, 0.16)",
            }}
          >
            {userData.initials}
          </div>
          <div className="relative z-10">
            <h2 className="text-xl font-semibold" style={{ color: tokens.ink }}>{userData.name}</h2>
            <p className="text-sm" style={{ color: tokens.muted }}>{userData.designation} - {userData.company}</p>
          </div>
        </div>

        <ProfileAccount
          user={userData}
          editing={editing}
          tokens={tokens}
          onEdit={() => setEditing(true)}
          onCancelEdit={() => setEditing(false)}
          onSave={handleSave}
        />
      </main>
    </div>
  );
};

const MyProfilePage = () => (
  <SidePanel>
    <MyProfileContent />
  </SidePanel>
);

export default MyProfilePage;

